import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
import type { Dayjs } from 'dayjs';
import { task } from 'ember-concurrency';
import Fuse from 'fuse.js';
import TdExpandableCell from 'lumen/components/settings/activity/td-expandable-cell';
import type {
  ActivityLogData,
  ActivityModel,
  UserListItem,
} from 'lumen/routes/hotel/settings/activity';
import {
  ACTION_STATUS_TRANSLATION_KEYS,
  ACTION_STATUSES,
  type ActionStatus,
  ACTIVITY_ACTION_TRANSLATION_KEYS,
  ACTIVITY_CATEGORY_TRANSLATION_KEYS,
  type ActivityActionTranslationKeys,
  type ActivityCategory,
} from 'lumen/utils/constants/activity-action-constants';
import { timeout } from 'lumen/utils/waiters';

const LIGHTHOUSE_STAFF_USERNAME = 'lighthouse_staff';
const SYSTEM_USERNAME = 'system';

const ACTOR_TYPE_TRANSLATION_KEYS: Record<string, string> = {
  [LIGHTHOUSE_STAFF_USERNAME]:
    'settings.activity_history.user.lighthouse_staff',
  [SYSTEM_USERNAME]: 'settings.activity_history.user.system',
};

const COLUMNS = {
  ACTION: 'action_status',
  ACTIVITY: 'activity',
  CATEGORY: 'category',
  TIMESTAMP: 'timestamp',
  TIMESTAMP_FORMATTED: 'timestampFormatted',
  USER_NAME: 'userName',
  USER_NAME_FORMATTED: 'userNameFormatted',
  LOCATION: 'location',
} as const;

interface ActivityRow extends CustomEmberTableRow {
  _action?: string;
  _timestampMinute?: string;
  _userId?: number;
  children?: ActivityRow[];
  id?: string;
  isCollapsed?: boolean;
  isHeader?: boolean;
}

type UserActorOption = Option & { id: number };
type NonUserActorOption = Option & { id: string };
type UserOption = UserActorOption | NonUserActorOption;

type ActionStatusOption = Option & {
  id: ActionStatus | null;
};

type CategoryOption = Option & {
  id: ActivityCategory;
};

export default class HotelSettingsActivityController extends Controller {
  declare model: ActivityModel;
  queryParams = [
    'actionStatus',
    'actorTypes',
    'category',
    'startDate',
    'endDate',
    'searchKeys',
    'userIds',
    'page',
    'pageSize',
  ];

  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked actionStatus: ActionStatus | null = null;
  @tracked actorTypes: string[] = [];
  @tracked category: ActivityCategory = 'account';
  @tracked endDate: string | null = null;
  @tracked expandedRowIds: string[] = [];
  @tracked page = 1;
  @tracked pageSize = 100;
  @tracked searchInputValue = '';
  @tracked searchKeys: string[] = [];
  @tracked startDate: string | null = null;
  @tracked userIds: number[] = [];
  @tracked userSearchTerm = '';

  initialSorts = [
    {
      valuePath: COLUMNS.TIMESTAMP,
      isAscending: false,
    },
  ];
  pageSizeOptions = [100, 250, 500, 1000];

  debouncedSearchTask = task({ restartable: true }, async (value: string) => {
    await timeout();

    if (!value.trim()) {
      this.searchKeys = [];
    } else {
      const results = this.fuse.search(value);
      this.searchKeys = results.map((result) => result.item.key);
    }

    this.page = 1;
  });

  get actionSearchItems(): { key: string; label: string }[] {
    return Object.entries(ACTIVITY_ACTION_TRANSLATION_KEYS).map(
      ([key, translationKey]) => {
        const raw = this.intl.getTranslation(
          translationKey,
          this.intl.primaryLocale!,
        );
        const label =
          raw !== undefined ? raw.replace(/\{[^}]+\}/g, '').trim() : key;
        return { key, label };
      },
    );
  }

  @cached
  get actionStatusOptions(): ActionStatusOption[] {
    const allOption: ActionStatusOption = {
      label: this.intl.t('settings.activity_history.filter.all_actions'),
      id: null,
    };
    const statusOptions = ACTION_STATUSES.map((id) => ({
      label: this.intl.t(ACTION_STATUS_TRANSLATION_KEYS[id]),
      id,
    }));
    return [allOption, ...statusOptions];
  }

  get activityLogsData(): ActivityLogData | undefined {
    return this.model.activityLogsTask.lastSuccessful?.value ?? undefined;
  }

  @cached
  get categoryOptions(): CategoryOption[] {
    if (!this.activityLogsData?.meta.available_categories) {
      return [];
    }

    // TODO CPS-621: Enable "All categories" option once backend supports filtering by all categories (currently it requires a category to be specified)
    // const allCategoriesOption: CategoryOption = {
    //   label: this.intl.t('events.filter.all_categories'),
    // };

    const categoryOptions = this.activityLogsData.meta.available_categories
      .map((category) => {
        const translationKey = ACTIVITY_CATEGORY_TRANSLATION_KEYS[category];
        return {
          label: translationKey ? this.intl.t(translationKey) : category,
          id: category,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));

    return categoryOptions;
  }

  get columns(): CustomEmberTableColumn[] {
    return [
      {
        displayValuePath: COLUMNS.TIMESTAMP_FORMATTED,
        name: this.intl.t('settings.activity_history.column.date'),
        valuePath: COLUMNS.TIMESTAMP,
        tdComponent: TdExpandableCell,
        textAlign: 'left' as const,
        width: 200,
      },
      {
        name: this.intl.t('settings.activity_history.column.location'),
        valuePath: COLUMNS.LOCATION,
        tdComponent: TdExpandableCell,
        textAlign: 'left' as const,
        isSortable: false,
        width: 160,
      },
      {
        displayValuePath: COLUMNS.USER_NAME_FORMATTED,
        name: this.intl.t('settings.activity_history.column.user'),
        valuePath: COLUMNS.USER_NAME,
        tdComponent: TdExpandableCell,
        textAlign: 'left' as const,
        width: 220,
      },
      {
        name: this.intl.t('settings.activity_history.column.category'),
        valuePath: COLUMNS.CATEGORY,
        textAlign: 'left' as const,
        width: 130,
      },
      {
        name: this.intl.t('settings.activity_history.column.action'),
        valuePath: COLUMNS.ACTION,
        textAlign: 'left' as const,
        width: 100,
      },
      {
        name: this.intl.t('settings.activity_history.column.activity'),
        valuePath: COLUMNS.ACTIVITY,
        textAlign: 'left' as const,
        isSortable: false,
      },
    ];
  }

  get currentPageSizeOption(): { label: string; value: number } {
    return {
      value: this.pageSize,
      label: this.intl.t('general.page_no_of_rows', {
        noOfRows: this.intl.formatNumber(this.pageSize),
      }),
    };
  }

  get dateRange(): { end: Dayjs; start: Dayjs } {
    const today = this.hotelAndMarketService.getLocalTime();
    return {
      start: this.startDate
        ? this.hotelAndMarketService.toLocalTime(this.startDate)
        : today.subtract(1, 'month'),
      end: this.endDate
        ? this.hotelAndMarketService.toLocalTime(this.endDate)
        : today,
    };
  }

  @cached
  get filteredUserOptions(): UserOption[] {
    if (!this.userSearchTerm) {
      return this.userOptions;
    }

    const searchLower = this.userSearchTerm.toLowerCase();
    return this.userOptions.filter(
      (option) =>
        option.label.toLowerCase().includes(searchLower) ||
        option.unsafe_subLabel?.toLowerCase().includes(searchLower),
    );
  }

  get formattedActivityLogs(): ActivityRow[] {
    if (this.isActivityLogsRunning || this.hasNoSearchMatch) {
      return [];
    }

    const data = this.activityLogsData;
    if (!data) {
      return [];
    }

    return data.activityLogs.map((log, index) => {
      const utcDate = log.timestamp as Dayjs; // Should never be null

      // Convert UTC timestamp to hotel's local timezone
      const timezone = this.hotelAndMarketService.timezone;
      const localDate = utcDate.utcOffset(timezone * 60);
      const gmtOffset = `GMT${timezone >= 0 ? '+' : ''}${timezone}`;
      const translationKey = ACTIVITY_ACTION_TRANSLATION_KEYS[log.action?.key];
      const isCollapsed = !this.expandedRowIds.includes(index.toString());

      // Create header row
      const headerRow: ActivityRow = {
        [COLUMNS.TIMESTAMP_FORMATTED]: this.intl.t(
          'settings.activity_history.expanded.what',
        ),
        [COLUMNS.LOCATION]: this.intl.t(
          'settings.activity_history.expanded.from',
        ),
        [COLUMNS.USER_NAME_FORMATTED]: this.intl.t(
          'settings.activity_history.expanded.to',
        ),
        isHeader: true,
      };

      // Create data rows for each change
      const dataRows: ActivityRow[] = Object.entries(log.changes ?? {}).map(
        ([what, [from, to]]) => ({
          [COLUMNS.TIMESTAMP_FORMATTED]: what,
          [COLUMNS.LOCATION]: from ?? this.intl.t('general.missing'),
          [COLUMNS.USER_NAME_FORMATTED]: to ?? this.intl.t('general.missing'),
        }),
      );
      const isStaffUser = log.userName === LIGHTHOUSE_STAFF_USERNAME;
      const isSystemUser = log.userName === SYSTEM_USERNAME;

      let displayUserName = log.userName;
      if (isStaffUser) {
        displayUserName = this.intl.t(
          'settings.activity_history.user.lighthouse_staff',
        );
      } else if (isSystemUser) {
        displayUserName = this.intl.t('settings.activity_history.user.system');
      }

      return {
        [COLUMNS.ACTION]: log.actionStatus
          ? this.intl.t(ACTION_STATUS_TRANSLATION_KEYS[log.actionStatus])
          : this.intl.t('general.missing'),
        [COLUMNS.ACTIVITY]: log.action?.key
          ? this.translateActivityKey(
              translationKey,
              log.action.key,
              log.action.values,
            )
          : this.intl.t('general.missing'),
        [COLUMNS.TIMESTAMP]: utcDate.toISOString(),
        [COLUMNS.TIMESTAMP_FORMATTED]: {
          primary: localDate.format('DD MMM YYYY'),
          secondary: `${localDate.format('HH:mm')} ${gmtOffset}`,
        },
        [COLUMNS.LOCATION]: {
          primary: log.location ?? this.intl.t('general.missing'),
          secondary: log.ipAddress,
        },
        [COLUMNS.USER_NAME]: displayUserName,
        [COLUMNS.USER_NAME_FORMATTED]: {
          primary: displayUserName,
          secondary: isStaffUser ? null : log.userEmail,
        },
        [COLUMNS.CATEGORY]: this.intl.t(
          ACTIVITY_CATEGORY_TRANSLATION_KEYS[log.category],
        ),
        _userId: log.userId,
        _action: log.action?.key,
        _timestampMinute: utcDate.format('YYYY-MM-DD HH:mm'),
        isCollapsed,
        ...(dataRows.length > 0 && { children: [headerRow, ...dataRows] }),
      };
    });
  }

  get fuse(): Fuse<{ key: string; label: string }> {
    return new Fuse(this.actionSearchItems, {
      keys: ['label'],
      ignoreLocation: true,
      threshold: 0.1,
    });
  }

  get hasNoSearchMatch(): boolean {
    return (
      this.searchInputValue.trim().length > 0 &&
      this.searchKeys.length === 0 &&
      !this.debouncedSearchTask.isRunning
    );
  }

  get isActivityLogsRunning(): boolean {
    return this.model.activityLogsTask.isRunning ?? false;
  }

  get selectedActionStatus(): ActionStatusOption {
    return (
      this.actionStatusOptions.find(
        (option) => option.id === this.actionStatus,
      ) ?? this.actionStatusOptions[0]!
    );
  }

  get selectedCategory(): CategoryOption | null {
    if (!this.category) {
      // Return "All categories" option when no category is selected
      return this.categoryOptions[0] ?? null;
    }
    return (
      this.categoryOptions.find(
        (categoryOption) => categoryOption.id === this.category,
      ) ?? null
    );
  }

  get selectedUsers(): UserOption[] {
    if (this.userIds.length === 0 && this.actorTypes.length === 0) {
      return this.userOptions;
    }
    return this.userOptions.filter((userOption) => {
      const { id } = userOption;
      return typeof id === 'string'
        ? this.actorTypes.includes(id)
        : this.userIds.includes(id);
    });
  }

  get showingText(): string {
    if (this.totalRecords === 0) {
      return '';
    }
    const startIndex = (this.page - 1) * this.pageSize + 1;
    const endIndex = Math.min(this.page * this.pageSize, this.totalRecords);

    return this.intl.t('general.page_showing', {
      currentPage: this.intl.formatNumber(startIndex),
      lastPage: this.intl.formatNumber(endIndex),
      totalRecords: this.intl.formatNumber(this.totalRecords),
    });
  }

  get today(): Dayjs {
    return this.hotelAndMarketService.getLocalTime();
  }

  get totalPages(): number {
    return Math.ceil(this.totalRecords / this.pageSize);
  }

  get totalRecords(): number {
    return this.activityLogsData?.meta.count ?? 0;
  }

  @cached
  get userOptions(): UserOption[] {
    const data = this.activityLogsData;
    if (!data?.meta.user_list) {
      return [];
    }

    return data.meta.user_list
      .map((user: UserListItem) => {
        if ('actor_type' in user) {
          const translationKey = ACTOR_TYPE_TRANSLATION_KEYS[user.actor_type];
          const label = translationKey
            ? this.intl.t(translationKey)
            : user.actor_type;
          return { label, unsafe_subLabel: undefined, id: user.actor_type };
        }

        return {
          label: user.name,
          unsafe_subLabel: user.email,
          id: user.user_id,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  private translateActivityKey(
    translationKey: (typeof ACTIVITY_ACTION_TRANSLATION_KEYS)[ActivityActionTranslationKeys],
    actionKey: ActivityActionTranslationKeys,
    values: Record<string, string>,
  ): string {
    try {
      return this.intl.t(translationKey, values);
    } catch (error) {
      this.errorTracking.captureException(error, { extra: { actionKey } });
      return this.intl.t('general.missing');
    }
  }

  @action
  onChangePageSize(pageSizeOption: { label: string; value: number }): void {
    this.pageSize = pageSizeOption.value;
    this.page = 1; // Reset to first page when changing page size

    this.usageAnalytics.track('Activity History Table Row Number Changed', {
      eventTrigger: 'table',
      product: 'activity-history',
      pageSize: pageSizeOption.value,
    });
  }

  @action
  onRowCollapseToggle(row: ActivityRow): void {
    const rowId = row.id;
    if (!rowId) {
      return;
    }

    const expandedSet = new Set(this.expandedRowIds);
    if (expandedSet.has(rowId)) {
      expandedSet.delete(rowId);
    } else {
      expandedSet.add(rowId);
    }

    this.expandedRowIds = Array.from(expandedSet);
  }

  @action
  resetUserSearch(): void {
    this.userSearchTerm = '';
  }

  @action
  searchUsers(searchTerm: string): void {
    this.userSearchTerm = searchTerm;
  }

  @action
  setPage(page: number): void {
    this.page = page;
  }

  @action
  updateDateRange(range: { end: Dayjs | null; start: Dayjs | null }): void {
    if (range.start) {
      this.startDate = range.start.format('YYYY-MM-DD');
      // If end is null, use start (single day selection)
      this.endDate = (range.end ?? range.start).format('YYYY-MM-DD');
      this.page = 1;

      this.usageAnalytics.track('Activity History Date Filtered', {
        eventTrigger: 'dropdown-menu',
        product: 'activity-history',
        startDate: this.startDate,
        endDate: this.endDate,
      });
    }
  }

  @action
  updateSearch(event: Event): void {
    const { value } = event.target as HTMLInputElement;
    this.searchInputValue = value;
    void this.debouncedSearchTask.perform(value);
  }

  @action
  updateSelectedActionStatus(selectedOption: ActionStatusOption): void {
    this.actionStatus = selectedOption.id;
    this.page = 1;

    this.usageAnalytics.track('Activity History Action Filtered', {
      eventTrigger: 'dropdown-menu',
      product: 'activity-history',
      action: selectedOption.id,
    });
  }

  @action
  updateSelectedCategory(selectedOption: CategoryOption): void {
    this.category = selectedOption.id;
    this.page = 1;

    this.usageAnalytics.track('Activity History Category Filtered', {
      eventTrigger: 'dropdown-menu',
      product: 'activity-history',
      category: selectedOption?.id || 'all',
    });
  }

  @action
  updateSelectedUsers(selectedOptions: UserOption[]): void {
    const isAll = selectedOptions.length === this.userOptions.length;
    if (isAll) {
      this.userIds = [];
      this.actorTypes = [];
    } else {
      this.userIds = selectedOptions
        .filter(
          (option): option is UserActorOption => typeof option.id === 'number',
        )
        .map((option) => option.id);
      this.actorTypes = selectedOptions
        .filter(
          (option): option is NonUserActorOption =>
            typeof option.id === 'string',
        )
        .map((option) => option.id);
    }
    this.userSearchTerm = '';
    this.page = 1;

    // Determine if filtering by current user or other users
    const isCurrentUserSelected =
      selectedOptions.length === 1 &&
      selectedOptions[0]?.unsafe_subLabel ===
        this.hotelAndMarketService.user.email;

    this.usageAnalytics.track('Activity History User Filtered', {
      eventTrigger: 'dropdown-menu',
      product: 'activity-history',
      userCount: selectedOptions.length,
      isCurrentUser: isCurrentUserSelected,
    });
  }

  @action
  usersLabelWithCount(count: number): string {
    return this.intl.t('settings.activity_history.filter.users_count', {
      count,
    });
  }
}
